import React, { useState } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  Box,
  Link,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from 'components';
import { useIsMobile } from 'hooks';

import { useLocation } from 'third-party';

import { NavigationItems } from 'types/app';

type Props = {
  item: NavigationItems;
  isCollapsed: boolean;
};

export const NavigationLink: React.FC<Props> = ({ item, isCollapsed }) => {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);

  // Inline component (e.g. Divider, OrganizationMenuHeader)
  if (item.component) {
    return <Box sx={{ padding: '4px 0' }}>{item.component}</Box>;
  }

  const isActive = pathname === item.url?.();

  return (
    <div id={item.id}>
      <Link
        href={item.url?.() || '#'}
        underline="none"
        target={item.openInNewTab ? '_blank' : undefined}
        rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
      >
        <ListItem disablePadding>
          <Tooltip
            disableInteractive={!isCollapsed}
            disableHoverListener={!isCollapsed}
            disableFocusListener={!isCollapsed}
            title={
              <Typography
                variant="body2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {item.text}
                {item.openInNewTab && (
                  <OpenInNewIcon sx={{ fontSize: '12px' }} />
                )}
              </Typography>
            }
            placement="right"
            arrow
          >
            <ListItemButton
              selected={isActive}
              disableRipple
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              sx={{
                padding: isMobile ? '10px 20px' : '10px 24px',
                borderLeft: '3px solid transparent',
                borderRadius: 0,
                gap: '12px',
                transition: 'all 0.3s ease',
                color: 'text.secondary',
                fontWeight: 500,
                // Hover: text-dark + bg-light, border unchanged
                '&:hover': {
                  bgcolor: '#F9FAFB',
                  color: '#32363F',
                },
                // Active state
                '&.Mui-selected': {
                  bgcolor: 'rgba(230, 106, 16, 0.05)',
                  color: 'primary.main',
                  fontWeight: 600,
                  borderLeftColor: 'primary.main',
                },
                // Active + hover: keep orange bg, keep border
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(230, 106, 16, 0.05)',
                  color: 'primary.main',
                },
              }}
            >
              {item.icon && (
                <ListItemIcon
                  sx={{
                    minWidth: 'unset',
                    color: 'inherit',
                    fontSize: '20px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                      color: 'inherit',
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              )}

              {!isCollapsed && (
                <ListItemText
                  primary={
                    <span
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <span>{item.text}</span>
                      {item.openInNewTab && isHovered && (
                        <OpenInNewIcon sx={{ fontSize: '14px', marginLeft: 'auto' }} />
                      )}
                    </span>
                  }
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: 'inherit',
                    fontFamily: 'Montserrat, sans-serif',
                    color: 'inherit',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </Link>
    </div>
  );
};
